import { useAuth } from 'components/AuthProvider'
import {
  DataPoint,
  GraphPeriodType,
  buildDataHistoricalHoldingBalances
} from 'components/PerformanceGraph'
import { useWalletPerformanceHistory } from 'hooks/useWallet'
import { useProductPerformance } from 'hooks/useInventory'
import useProducts from 'hooks/useProducts'
import useVault from 'hooks/useVault/useVault'
import { useEffect, useMemo, useState } from 'react'
interface Props {
  productId?: number
  graphPeriod: GraphPeriodType
}

export function usePerformanceGraph({ productId, graphPeriod }: Props) {
  const { activatedVaultId } = useVault()

  const { products } = useProducts()

  const { user } = useAuth()

  const defaultVaultId = useMemo(() => {
    return user?.vaults?.[0]?.id
  }, [user?.vaults])

  const [totalProductPerformanceToday, setTotalProductPerformanceToday] =
    useState<number>(0)

  const productIds = productId
    ? [productId]
    : products.map(({ id }) => Number(id))

  // Fetch performance for all products to get today's value
  const { refetch: fetchProductPerformance } = useProductPerformance(
    productId ?? 0
  )

  const vaultIds = activatedVaultId
    ? [Number(activatedVaultId)]
    : defaultVaultId
    ? [Number(defaultVaultId)]
    : undefined

  const { data: historicalPerformance, isLoading: loading } =
    useWalletPerformanceHistory(
      productId ? [productId] : undefined,
      vaultIds,
      graphPeriod
    )

  useEffect(() => {
    Promise.all(
      productIds.map(() =>
        fetchProductPerformance().then(({ data }) =>
          Number(data?.currentValue ?? 0)
        )
      )
    ).then(values =>
      setTotalProductPerformanceToday(values.reduce((sum, v) => sum + v, 0))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  const builtData: DataPoint[] = useMemo(() => {
    return buildDataHistoricalHoldingBalances({
      data: historicalPerformance,
      productPriceToday: totalProductPerformanceToday,
      graphPeriod
    })
  }, [graphPeriod, historicalPerformance, totalProductPerformanceToday])

  return {
    builtData,
    loading
  }
}

export default usePerformanceGraph
